import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { setActiveTender, setTermYearMapping, mergeTermYearMappings } from '../lib/termLabel';
import { seriesToTermYearMapping, proposeTermSeries, cleanTermSeries, type TenderTermSeries } from '../lib/tenderTermSeries';
import { saveTenderTermSeries } from '../services/tenderWorkflowService';
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import {
  DocumentFormContextType,
  DocumentFormProviderProps,
  PromptTemplate,
  ClientFormData,
  ReferenceDocument,
  AiPrompts,
  ProjectInfo,
} from "../types/document-form";
import { Site } from "../types/siteTypes";
import { Offer } from "../types/offerTypes";
import { OfferDetailsData, OfferCostData, createDefaultContractData, calculateCompletionPercentage } from "../types/offerDetailsTypes";
import { saveOfferCostBreakdowns } from "../services/offerCostBreakdownService";
import { saveOffers, getUserOrganizationId, createOrGetTender, saveTenderSites, getOffers, updateTenderStep } from "../services/tenderApiService";
import {
  generateDocument,
  prepareDocumentData,
} from "../utils/documentGeneration";
import {
  enhancedClientService,
  type EnhancedClient,
} from "../services/enhancedClientService";
import { useUser } from "../../../foundation/altzero_base/base/contextapi/UserContext";
import {
  saveTenderWorkflowState,
  loadTenderWorkflowState,
  createNewTender,
  WORKFLOW_STEPS,
  type TenderWorkflowData
} from "../services/tenderWorkflowService";

// Create context with default values
export const DocumentFormContext = createContext<DocumentFormContextType>(
  {} as DocumentFormContextType
);

// Custom hook to use the context
export const useDocumentForm = () => useContext(DocumentFormContext);

// Import template service
import { getAllTemplates } from "../services/documentLibraryService";

// Context provider component
export const DocumentFormProvider: React.FC<DocumentFormProviderProps> = ({
  children,
  preSelectedTemplate,
}) => {
  const { toast } = useToast();
  const { user } = useUser();

  // Clear session data when starting fresh
  const clearSessionData = () => {
    console.log("🧹 Clearing session data for fresh start...");

    const keysToClean = [
      // US-030: Removed - now database-persisted: "scopingai_clientData", "scopingai_sites", "scopingai_offers", "scopingai_proposal_data"
      "scopingai_selectedClient",
      "scopingai_projectInfo",
      "selectedTemplateFromLibrary",
      "scopingai_generated_url",
      "autoTriggerRegeneration",
      "scopingai_session_id",
      "scopingai_session_timestamp"
    ];

    keysToClean.forEach(key => {
      if (sessionStorage.getItem(key)) {
        sessionStorage.removeItem(key);
        console.log(`✅ Cleared session key: ${key}`);
      }
    });

    // Also clear any document-specific keys
    const allKeys = Object.keys(sessionStorage);
    const documentKeys = allKeys.filter(key =>
      key.startsWith("document_") || key.includes("_branding")
    );

    documentKeys.forEach(key => {
      sessionStorage.removeItem(key);
      console.log(`✅ Cleared document key: ${key}`);
    });

    console.log("✅ Session data cleanup completed");
  };

  // Session management for proper data persistence
  const initializeSession = () => {
    // Check if we have a valid active session
    const currentSessionId = sessionStorage.getItem("scopingai_session_id");
    const sessionTimestamp = sessionStorage.getItem("scopingai_session_timestamp");

    // If no session or session is older than 1 hour, start fresh
    const now = Date.now();
    const sessionAge = sessionTimestamp ? now - parseInt(sessionTimestamp) : Infinity;
    const maxSessionAge = 60 * 60 * 1000; // 1 hour

    if (!currentSessionId || sessionAge > maxSessionAge) {
      // Clear all old data and start fresh
      clearSessionData();

      // Create new session
      const newSessionId = `session_${now}_${Math.random().toString(36).substring(2, 11)}`;
      sessionStorage.setItem("scopingai_session_id", newSessionId);
      sessionStorage.setItem("scopingai_session_timestamp", now.toString());

      console.log("🆕 Started new session:", newSessionId);
      return false; // No data to restore
    }

    console.log("🔄 Continuing existing session:", currentSessionId);
    return true; // Can restore data
  };

  // State variables - Handle step initialization with library return
  const getInitialStep = () => {
    // Initialize session first
    const hasValidSession = initializeSession();

    // Check if returning from library with a selected template
    const returnFromLibrary = sessionStorage.getItem(
      "selectedTemplateFromLibrary"
    );
    const savedClientData = sessionStorage.getItem("scopingai_clientData");

    if (returnFromLibrary && hasValidSession) {
      // If we have client data, return to template selection step (step 2)
      // Otherwise, start from step 1
      const targetStep = savedClientData ? 2 : 1;
      console.log(`🔄 Returning from library to step ${targetStep}`);
      return targetStep;
    }

    // Always start from step 1 for better UX
    const initialStep = 1;
    console.log(
      "🎯 DocumentFormContext - Initial step:",
      initialStep,
      "URL:",
      window.location.href
    );
    return initialStep;
  };

  const [currentStep, setCurrentStep] = useState(getInitialStep());
  const [completedSteps, setCompletedSteps] = useState<number[]>([]); // US-031: Track completed workflow steps
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingClient, setIsSavingClient] = useState(false);
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState(() => {
    const initialTemplate = preSelectedTemplate?.type === "existing_proposal" ? preSelectedTemplate.id : "";
    console.log("🎯 DocumentFormContext initialized with selectedTemplate:", {
      preSelectedTemplate,
      initialTemplate,
      type: preSelectedTemplate?.type,
    });
    return initialTemplate;
  });
  const [referenceDocument, setReferenceDocument] =
    useState<ReferenceDocument | null>(null);

  // Templates state
  const [templates, setTemplates] = useState<any[]>([]);
  const [aiPrompts, setAiPrompts] = useState<AiPrompts>({
    styleGuidance: "",
    contentFocus: "",
    additionalInstructions: "",
  });
  const [requirementsOption, setRequirementsOption] = useState("requirements");
  const [requirementsText, setRequirementsText] = useState("");
  const [selectedKnowledgeDocuments, setSelectedKnowledgeDocuments] = useState<
    string[]
  >([]);
  const [documentRequirements, setDocumentRequirements] = useState<
    Record<string, string>
  >({});

  // Generation tracking state
  const [generationStatus, setGenerationStatus] = useState(
    "Starting generation..."
  );
  const [generationProgress, setGenerationProgress] = useState(0);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [hasRedirected, setHasRedirected] = useState(false);

  // Add progress tracking state
  const [lastProgress, setLastProgress] = useState<number>(0);



  // Reset progress tracking when generation starts
  const resetProgressTracking = () => {
    setLastProgress(0);
    setGenerationProgress(0);
  };

  // Ensure monotonic progress (only increases) - Fixed to allow 100%
  const updateProgress = (newProgress: number, message?: string) => {
    const sanitizedProgress = Math.max(0, Math.min(newProgress || 0, 100));

    // Only update if progress actually increases or if it's the first update
    if (sanitizedProgress >= lastProgress || lastProgress === 0) {
      setLastProgress(sanitizedProgress);
      setGenerationProgress(sanitizedProgress);

      if (message) {
        setGenerationStatus(message);
      }

      console.log(
        `📊 Progress updated: ${lastProgress}% → ${sanitizedProgress}%`
      );
    } else {
      console.log(
        `⚠️ Ignored backwards progress: ${sanitizedProgress}% (current: ${lastProgress}%)`
      );
    }
  };

  // EventSource reference
  const eventSourceRef = useRef<EventSource | null>(null);

  // Client state
  const [clients, setClients] = useState<EnhancedClient[]>([]);
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<EnhancedClient | null>(
    null
  );
  const [clientData, setClientData] = useState<ClientFormData>({
    id: undefined,
    name: "",
    contactPerson: "",
    email: "",
    phone: "",
    industry: "",
    company: "",
  });
  const [isNewClient, setIsNewClient] = useState(false);
  const [sites, setSites] = useState<Site[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);

  // Ref to store current offers for stable callbacks
  const offersRef = useRef<Offer[]>([]);



  // Offer details data state - for monthly breakdown collection
  const [offerDetailsData, setOfferDetailsData] = useState<OfferDetailsData>({
    offerCostData: [],
    isStepComplete: false,
    completionPercentage: 0,
  });

  // Selected client ID state - persisted across steps
  const [selectedClientId, setSelectedClientId] = useState<string>("");

  // Selected site ID state - persisted across steps using Context API
  const [selectedSiteId, setSelectedSiteId] = useState<string>("");

  // Tender state - for saving proposal/tender
  const [tenderName, setTenderName] = useState("");
  const [tenderId, setTenderId] = useState<string | null>(null);

  // Tell the label layer which tender is on screen, the moment this context knows.
  //
  // Term -> year assignments are stored per tender (termLabel.ts) and read by ~45 label call
  // sites that only receive a bare `termMonths`. getOffers also names the tender, but it does
  // so inside an async fetch — so anything rendering between navigation and that fetch would
  // resolve against the tender the user just left. That was the "wde showed Year 5/1/2/3" bug.
  // Setting it here, synchronously with the state that defines the current tender, removes the
  // window rather than shrinking it.
  useEffect(() => {
    setActiveTender(tenderId);
  }, [tenderId]);

  // Publish the tender's term -> year assignment from the OFFERS THEMSELVES, whenever they
  // change — not from whichever fetch happened to load them.
  //
  /**
   * The tender's contract-term series — which month count each Year N is.
   *
   * Held here rather than derived per screen because it is a property of the TENDER: every
   * offer's terms are placed against the same series, which is what makes 4 and 16 land on
   * different pages. Persisted to tender.extn.term_series.
   */
  const [termSeries, setTermSeriesState] = useState<TenderTermSeries>({});

  /**
   * Set the tender's term series and persist it.
   *
   * Optimistic: state updates first so the panel and every label react immediately, then the
   * write goes out. A failed write is logged rather than thrown — losing the panel's value
   * mid-edit is worse than a series that needs re-saving, and the next save retries it.
   *
   * ⚠️ A MISSING TENDER ID USED TO MEAN "SAVED" ON SCREEN AND NOTHING IN THE DATABASE.
   *
   * `if (!tenderId) return` swallowed the write silently: the chip showed the new series, every
   * label re-keyed to it, and the reviewer had no way to know the tender still carried the old
   * one — or none at all — until the document printed against the wrong years. The state still
   * updates (so the work in progress is not thrown away), but the reviewer is now told.
   */
  const setTermSeries = useCallback(async (rawSeries: TenderTermSeries): Promise<void> => {
    // The panel's inputs hold '' while a year is being retyped, and closing the popover mid-edit
    // committed that '' straight to jsonb. Everything downstream drops it again, so the row read
    // back is not the row written — a year that looks set in the database and is not.
    const series = cleanTermSeries(rawSeries);

    setTermSeriesState(series);
    if (!tenderId) {
      console.error('Term series not persisted: the tender has no id yet.');
      toast({
        title: 'Contract terms not saved yet',
        description: 'This tender has not been created yet. Save the tender, then set the '
          + 'contract terms again.',
        variant: 'destructive',
      });
      return;
    }
    const result = await saveTenderTermSeries(tenderId, series);
    if (!result.success) {
      console.error('Could not persist the tender term series:', result.error);
      toast({
        title: 'Term series not saved',
        description: 'The contract terms for this tender could not be saved. Try again.',
        variant: 'destructive',
      });
    }
  }, [tenderId, toast]);

  /**
   * The series this tender's offers imply — the term set the most offers agree on.
   *
   * Offered as a seed for the panel so the ordinary case is a confirmation rather than typing.
   * Read from available_terms first and the priced term keys second, because available_terms
   * has been wrong before (it fell back to CONTRACT_LENGTHS whenever the keys carried a
   * _smooth/_stepped suffix).
   */
  const proposedTermSeries = useCallback((): TenderTermSeries => proposeTermSeries(
    (offers ?? []).map((o: any) => {
      const declared = Array.isArray(o?.availableTerms) ? o.availableTerms
        : Array.isArray(o?.available_terms) ? o.available_terms : null;
      if (declared && declared.length > 0) {
        return declared.map((t: unknown) => parseInt(String(t), 10)).filter(Number.isFinite);
      }
      const terms = o?.multiYearRateData?.contract_terms
        ?? o?.multi_year_rate_data?.contract_terms
        ?? o?.extn?.contract_terms;
      if (!terms || typeof terms !== 'object') return null;
      return Object.keys(terms)
        .map(k => parseInt(k, 10))
        .filter(n => Number.isFinite(n) && n > 0);
    }),
  ), [offers]);

  // ⚠️ This is why the assignment kept going missing. It used to be published only inside
  // getOffers, so it depended on a fetch having run. Opening a tender from the list worked,
  // but Step 3 -> Next -> Step 4 builds the offers in memory and does not necessarily refetch,
  // so nothing published anything and every label fell back to derived — the 1/1/2/3 that made
  // an assigned year look ignored. Keying off the offers state covers every path that can
  // produce offers: the fetch, the Step 3 conversion, an in-place edit, demo data.
  useEffect(() => {
    if (!tenderId) return;
    // ⚠️ THE SERIES GOES FIRST — THIS MAPPING IS TENDER-WIDE, NOT PER OFFER.
    //
    // It used to go last so a per-offer assignment would win. But what is published here is ONE
    // mapping for the whole tender (termYearsByTender), and formatTermLabel reads it with no
    // offer in hand — so one offer's label re-numbered that term on every screen, for every
    // retailer.
    //
    // Same defect as the document generator, and it bit there first: a 16 / 28 / 40 / 52 tender
    // whose Nectr row carried a stale { "16": 2, "28": 1 } printed the 28-month term on the
    // Year 1 page and the 16-month figures on Year 2, while the other three retailers had no
    // labels at all. Both places now let the series answer every term it describes; a per-offer
    // label fills in only a length the series does not have — the retailer who answers a
    // 16-month request with 15, which is the case the override exists for.
    //
    // A tender with no series is unchanged: the offers remain the only source, same order.
    setTermYearMapping(
      mergeTermYearMappings([
        seriesToTermYearMapping(termSeries),
        ...(offers ?? []).map((o: any) =>
          o?.multiYearRateData?.term_year_labels
          ?? o?.multi_year_rate_data?.term_year_labels
          ?? o?.extn?.term_year_labels
          ?? null),
      ]),
      tenderId,
    );
  }, [offers, tenderId, termSeries]);
  const [tenderSiteIds, setTenderSiteIds] = useState<string[]>([]);

  // Workflow state management
  const [isLoadingTender, setIsLoadingTender] = useState(false);
  const [workflowData, setWorkflowData] = useState<TenderWorkflowData>({});

  // Salesforce import state — shared across steps
  const [sfOpportunityId, setSfOpportunityId] = useState<string>('');
  const [sfTenderId, setSfTenderId] = useState<string>('');
  const [sfImportExists, setSfImportExists] = useState<boolean>(false);
  const [sfImportedAt, setSfImportedAt] = useState<string | null>(null);

  // Tender details state (collected in TenderDetailsStep)
  const [tenderDetails, setTenderDetails] = useState({
    type: "",
    account_name: "",
    entity_name: "",
    notes: "",
    // Default values for all sites
    abn: "",
    acn: "",
    due_at: "",
    current_contract_expiry: "",
    current_meter_provider: "",
    current_retailer: "",
    dma_provider: "",
    retailer_comms: "",
    create_dma: false,
    request_meter_data: false,
    request_bills: false,
    acquire_ced: false,
    addr_street: "",
    addr_city: "",
    addr_state: "",
    addr_postcode: "",
    addr_country: "",
    sf_opportunity_id: "",
  });

  // Prompt template state - load from database, no hardcoded data
  const [promptTemplates, setPromptTemplates] = useState<PromptTemplate[]>([]);
  const [promptSearchOpen, setPromptSearchOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<PromptTemplate | null>(
    null
  );
  const [promptData, setPromptData] = useState<PromptTemplate>({
    id: "",
    name: "",
    description: "",
    content: "",
    variables: [],
  });
  const [isNewPrompt, setIsNewPrompt] = useState(false);
  const [isPromptDialogOpen, setIsPromptDialogOpen] = useState(false);

  // Logo and branding state
  const [documentLogo, setDocumentLogo] = useState<{
    src: string;
    position: string;
    size: string;
    opacity: number;
    showOnAllPages: boolean;
  } | null>(null);
  const [documentBranding, setDocumentBranding] = useState<{
    primaryColor?: string;
    secondaryColor?: string;
    brandTitle?: string;
    tagline?: string;
  } | null>(null);

  // Project info state
  const [projectInfo, setProjectInfo] = useState<ProjectInfo>({
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    budget: "custom", // Default to custom for user input
    budgetMin: undefined,
    budgetMax: undefined,
  });

  // Add a useEffect to clear requirements text when switching to upload
  useEffect(() => {
    if (requirementsOption === "upload") {
      // Add a small delay to ensure the warning is shown first
      const timer = setTimeout(() => {
        setRequirementsText("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [requirementsOption]);

  // Load clients when user is authenticated
  useEffect(() => {
    if (user && !isLoadingClients && clients.length === 0) {
      loadClients();
    }
  }, [user]);

  // Load templates on component mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        // Only load templates if user is available
        if (!user?.id) {
          console.log("⏳ Waiting for user to load templates...");
          return;
        }

        console.log("🔄 Loading templates for user:", user.id);
        const allTemplates = await getAllTemplates(user.id);
        console.log("📋 Loaded templates:", allTemplates);
        console.log(
          "📋 Template structure check:",
          allTemplates.map((t) => ({
            id: t.id,
            title: t.title,
            sectionsCount: t.sections?.length,
          }))
        );
        setTemplates(allTemplates);
      } catch (error) {
        console.error("❌ Failed to load templates:", error);
      }
    };

    loadTemplates();
  }, [user?.id]); // Depend on user.id

  // Handle template selection from library return
  useEffect(() => {
    const selectedTemplateFromLibrary = sessionStorage.getItem(
      "selectedTemplateFromLibrary"
    );
    if (selectedTemplateFromLibrary && templates.length > 0) {
      console.log(
        "🔄 Applying template from library:",
        selectedTemplateFromLibrary
      );
      handleTemplateSelect(selectedTemplateFromLibrary);
      // Clear the sessionStorage after applying
      sessionStorage.removeItem("selectedTemplateFromLibrary");
    }
  }, [templates]); // Run when templates are loaded

  // NOTE: resuming a tender from ?tender=<id> is handled solely by DocumentsNew (the only
  // host of this provider), which calls loadTenderWorkflow for every such URL, with or
  // without editMode. A second resume effect used to live here and only skipped the
  // editMode case, so a plain ?tender=<id> URL ran two loaders concurrently: both flipped
  // isLoadingTender (so the loading screen cleared when the FIRST finished and steps
  // mounted with half-loaded sites/offers), and they disagreed on offer.siteId —
  // loadTenderData sets offers unmapped while loadTenderWorkflow maps them — so whichever
  // resolved last decided the convention. Removed; loadTenderWorkflow is the only loader.

  const loadTenderData = async (id: string) => {
    setIsLoadingTender(true);
    try {
      const result = await loadTenderWorkflowState(id);
      if (result.success && result.data) {
        const { currentStep, workflowData, tenderInfo, sites: loadedSites } = result.data;

        // Set IDs
        setTenderId(id);

        // Set Client Data
        if (tenderInfo.client) {
          const client = {
            id: tenderInfo.client.id,
            name: tenderInfo.client.name,
            // Other fields might be missing if not joined, but ID/Name is key
            contactPerson: '',
            email: '',
            phone: '',
            industry: '',
            company: tenderInfo.client.name
          };
          setClientData(client);
          setSelectedClient(client as any);
          setSelectedClientId(tenderInfo.client.id);
        }

        // Set Tender Details
        setTenderName(tenderInfo.opportunity_name);
        setTenderDetails({
          type: tenderInfo.type || '',
          account_name: tenderInfo.account_name || '',
          entity_name: tenderInfo.entity_name || '',
          notes: tenderInfo.notes || '',
          abn: tenderInfo.abn || '',
          acn: tenderInfo.acn || '',
          due_at: tenderInfo.due_at || '',
          current_contract_expiry: tenderInfo.current_contract_expiry || '',
          current_meter_provider: tenderInfo.current_meter_provider || '',
          current_retailer: tenderInfo.current_retailer || '',
          dma_provider: tenderInfo.dma_provider || '',
          retailer_comms: tenderInfo.retailer_comms || '',
          create_dma: tenderInfo.create_dma || false,
          request_meter_data: tenderInfo.request_meter_data || false,
          request_bills: tenderInfo.request_bills || false,
          acquire_ced: tenderInfo.acquire_ced || false,
          addr_street: tenderInfo.addr_street || '',
          addr_city: tenderInfo.addr_city || '',
          addr_state: tenderInfo.addr_state || '',
          addr_postcode: tenderInfo.addr_postcode || '',
          addr_country: tenderInfo.addr_country || '',
          sf_opportunity_id: tenderInfo.sf_opportunity_id || '',
        });

        // The tender's contract-term series, if it has one. Restored BEFORE the offers so the
        // label mapping is right the first time a screen renders rather than after a second
        // pass — an unset series leaves {} and every term falls back to the derived year,
        // exactly as before this existed.
        setTermSeriesState(
          (tenderInfo?.extn && typeof tenderInfo.extn === 'object'
            && tenderInfo.extn.term_series && typeof tenderInfo.extn.term_series === 'object')
            ? tenderInfo.extn.term_series
            : {},
        );

        // Set Sites
        if (loadedSites && loadedSites.length > 0) {
          const mappedSites: Site[] = loadedSites.map((s: any) => ({
            id: s.id,
            siteId: s.site_id,
            site_id: s.site_id, // Keep for backward compatibility if needed, but prefer siteId
            // name/nmi are nullable in the DB, so default them like every other string
            // field here — downstream render code calls .trim() on them.
            name: s.name || '',
            nmi: s.nmi || '',
            address: {
              street: s.addr_street || '',
              city: s.addr_city || '',
              state: s.addr_state || '',
              postcode: s.addr_postcode || '',
              country: s.addr_country || 'Australia',
            },
            annualKwhPeak: s.annual_kwh_peak,
            annualKwhOffpeak: s.annual_kwh_offpeak,
            annualKwhShoulder: s.annual_kwh_shoulder,
            dailyCharges: s.daily_charges || 0,
            nonEnergyCharges: s.non_energy_charges || 0,
            peakDemandKw: s.peak_demand_kw || undefined,
            currentContractRates: s.current_contract_rates || undefined,
            greenPower: s.green_power || false,
            documentFilePaths: s.document_file_paths || s.site?.document_file_paths || [],
            retailerNotes: s.retailer_notes || s.site?.retailer_notes || '',

            // Map overrides
            abn: s.abn,
            acn: s.acn,
            dueAt: s.due_at ? new Date(s.due_at) : undefined,
            currentContractExpiry: s.current_contract_expiry ? new Date(s.current_contract_expiry) : undefined,
            currentMeterProvider: s.current_meter_provider,
            currentRetailer: s.current_retailer,
            dmaProvider: s.dma_provider,
            retailerComms: s.retailer_comms,
            createDma: s.create_dma || false,
            requestMeterData: s.request_meter_data || false,
            requestBills: s.request_bills || false,
            acquireCed: s.acquire_ced || false,

            // Default energy details
            energyDetails: {
              offerExpiry: '',
              rateType: 'fixed',
              peakRate: 0,
              offpeakRate: 0,
              shoulderRate: 0,
              cPerKwh: 0,
              nonEnergyCharges: 0,
            },
            extn: s.extn || {},
            createdAt: s.created_at ? new Date(s.created_at) : new Date(),
            isExpanded: false,
            isNew: false,
            isValid: true
          }));
          setSitesWithPersistence(mappedSites);
          setTenderSiteIds(mappedSites.map(s => s.id!).filter(Boolean));
        }

        // Set Workflow Data
        setWorkflowData(workflowData);

        // Load Offers
        try {
          const offersResult = await getOffers(id);
          if (offersResult.success && offersResult.offers) {
            setOffers(offersResult.offers);
            console.log(`✅ Loaded ${offersResult.offers.length} offers for tender ${id}`);
          }
        } catch (offerError) {
          console.error("Failed to load offers:", offerError);
        }

        // Set Step
        setCurrentStep(currentStep || 1);

        toast({
          title: "Tender Resumed",
          description: `Loaded tender: ${tenderInfo.opportunity_name}`,
        });
      }
    } catch (error) {
      console.error("Error loading tender:", error);
      toast({
        title: "Error",
        description: "Failed to load tender data",
        variant: "destructive",
      });
    } finally {
      setIsLoadingTender(false);
    }
  };

  // Restore client data, project info, sites, and offers from database on mount
  // US-030: Removed sessionStorage - data now loaded from database (US-032)
  useEffect(() => {
    // US-030: sessionStorage restoration removed for:
    // - scopingai_clientData
    // - scopingai_sites
    // - scopingai_offers
    // - scopingai_proposal_data
    // Data restoration will be implemented in US-032 using tenderWorkflowService

    const savedProjectInfo = sessionStorage.getItem("scopingai_projectInfo");
    const savedOfferDetails = sessionStorage.getItem("scopingai_offer_details");

    if (savedProjectInfo) {
      try {
        const parsedProjectInfo = JSON.parse(savedProjectInfo);
        setProjectInfo(parsedProjectInfo);

        console.log(
          "🔄 Restored project info from sessionStorage:",
          parsedProjectInfo
        );
      } catch (error) {
        console.error("Error restoring project info:", error);
        sessionStorage.removeItem("scopingai_projectInfo");
      }
    } else {
      console.log("ℹ️ DocumentFormContext: No saved data found - will load from database in US-032");
    }

    if (savedOfferDetails) {
      try {
        const parsedOfferDetails = JSON.parse(savedOfferDetails);
        setOfferDetailsData(parsedOfferDetails);


      } catch (error) {
        console.error("❌ DocumentFormContext: Error restoring offer details:", error);
        sessionStorage.removeItem("scopingai_offer_details");
      }
    } else {
      console.log("ℹ️ DocumentFormContext: No saved offer details found in sessionStorage");
    }
  }, []);

  // Sync offersRef whenever offers state changes
  useEffect(() => {
    offersRef.current = offers;
  }, [offers]);


  // Initialize offer details data when offers change or when on step 5
  useEffect(() => {
    if (offers.length > 0) {
      initializeOfferDetailsData();
    }
  }, [offers]);

  // Ensure offer details data is initialized when user is on step 5.
  // offerCostData is optional-chained in BOTH the body and the deps array: the deps array
  // is evaluated on every render of this provider (the body guard does not protect it), and
  // offerDetailsData can be set straight from unvalidated tender.workflow_data JSONB or
  // sessionStorage, so a stored object missing offerCostData would otherwise throw during
  // render of the provider wrapping the whole page — surfacing as a blank error screen.
  useEffect(() => {
    if (currentStep === 5 && offers.length > 0 && (offerDetailsData.offerCostData?.length ?? 0) === 0) {
      initializeOfferDetailsData();
    }
  }, [currentStep, offers.length, offerDetailsData.offerCostData?.length ?? 0]);

  // Load clients from CRM
  const loadClients = async (): Promise<EnhancedClient[]> => {
    setIsLoadingClients(true);
    setClientsError(null);

    try {
      const clientsData = await enhancedClientService.getOrganizationContacts();
      setClients(clientsData);
      return clientsData;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to load clients";
      setClientsError(errorMessage);
      console.error("Error loading clients:", error);
      // Removed toast error to prevent UI spam
      return [];
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Handle client selection
  const handleClientSelect = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    if (client) {
      const newClientData = {
        id: client.id,
        name: client.name,
        contactPerson: client.contactPerson,
        email: client.email,
        phone: client.phone,
        industry: client.industry,
        company: client.company,
      };

      setClientData(newClientData);
      setSelectedClient(client);
      setIsNewClient(false);
      setClientSearchOpen(false);

      // Persist client data to sessionStorage for step navigation
      sessionStorage.setItem(
        "scopingai_clientData",
        JSON.stringify(newClientData)
      );
      sessionStorage.setItem(
        "scopingai_selectedClient",
        JSON.stringify(client)
      );
    }
  };

  // Handle client input changes
  const handleClientInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setClientData((prev) => ({ ...prev, [name]: value }));

    // If user is typing a new name, mark as new client and clear ID
    if (name === "name") {
      setClientData((prev) => ({ ...prev, id: undefined }));
      setIsNewClient(true);
      setSelectedClient(null);
    }
  };

  // Handle project info changes
  const handleProjectInfoChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    let fieldName = "";

    // Map form field IDs to state properties
    switch (id) {
      case "project-title":
        fieldName = "title";
        break;
      case "project-description":
        fieldName = "description";
        break;
      case "start-date":
        fieldName = "startDate";
        break;
      case "end-date":
        fieldName = "endDate";
        break;
      default:
        fieldName = id.replace("project-", "").replace("-", "");
    }

    const newProjectInfo = { ...projectInfo, [fieldName]: value };
    setProjectInfo(newProjectInfo);

    // Persist project info to sessionStorage
    sessionStorage.setItem(
      "scopingai_projectInfo",
      JSON.stringify(newProjectInfo)
    );
  };

  // Handle budget changes
  const handleBudgetChange = (value: string) => {
    const newProjectInfo = { ...projectInfo, budget: value };
    setProjectInfo(newProjectInfo);

    // Persist project info to sessionStorage
    sessionStorage.setItem(
      "scopingai_projectInfo",
      JSON.stringify(newProjectInfo)
    );
  };

  // Helper function to save sites to sessionStorage
  // US-030: Removed sessionStorage - data now persisted via database (tenderWorkflowService)
  const saveSitesToSession = (sitesData: Site[]) => {
    // sessionStorage removed - persistence handled by database in US-031/US-032
    console.log("💾 Sites data ready for database persistence:", sitesData);
  };

  // Helper function to save offers to sessionStorage
  // US-030: Removed sessionStorage - data now persisted via database (tenderWorkflowService)
  const saveOffersToSession = (offersData: Offer[]) => {
    // sessionStorage removed - persistence handled by database in US-031/US-032
    console.log("💾 Offers data ready for database persistence:", offersData);
  };

  // Helper function to save offer details data to sessionStorage
  const saveOfferDetailsToSession = (offerDetailsData: OfferDetailsData) => {
    try {
      sessionStorage.setItem("scopingai_offer_details", JSON.stringify(offerDetailsData));
      console.log("💾 Saved offer details to sessionStorage:", offerDetailsData);
    } catch (error) {
      console.error("❌ Error saving offer details to sessionStorage:", error);
    }
  };

  // Enhanced setSites that also saves to sessionStorage
  const setSitesWithPersistence = useCallback((value: React.SetStateAction<Site[]>) => {
    setSites((prev) => {
      const newValue = typeof value === 'function' ? (value as Function)(prev) : value;
      saveSitesToSession(newValue);
      return newValue;
    });
  }, []);

  // Enhanced setOffers that also saves to sessionStorage
  const setOffersWithPersistence = useCallback((value: React.SetStateAction<Offer[]>) => {
    setOffers((prev) => {
      const newValue = typeof value === 'function' ? (value as Function)(prev) : value;
      offersRef.current = newValue;
      saveOffersToSession(newValue);
      return newValue;
    });
  }, []);

  // Update specific offer's multiYearRateData - SIMPLIFIED: Always update ref and sessionStorage, state only on blur
  const updateOfferRateData = useCallback((offerId: string, multiYearRateData: any) => {
    // Update ref immediately (always available, doesn't cause re-render)
    const currentOffers = offersRef.current.length > 0 ? offersRef.current : offers;
    const updatedOffers = currentOffers.map(offer => {
      if (offer.id !== offerId) return offer;

      // Carry the term -> year assignment across a rate edit.
      //
      // ⚠️ multiYearRateData is REPLACED wholesale here, and the five OfferManagementCard
      // callers build their new object from the rate fields alone. So editing any rate in
      // Step 4 silently dropped term_year_labels and every label fell back to derived — the
      // assignment looked like it had been ignored when it had actually been destroyed.
      // Preserved only when the incoming object does not carry its own, so a deliberate change
      // still wins.
      const previous = (offer as any).multiYearRateData;
      const merged = (multiYearRateData && previous?.term_year_labels !== undefined
        && multiYearRateData.term_year_labels === undefined)
        ? { ...multiYearRateData, term_year_labels: previous.term_year_labels }
        : multiYearRateData;

      return { ...offer, multiYearRateData: merged, updatedAt: new Date() };
    });
    offersRef.current = updatedOffers;

    // Save to sessionStorage immediately (doesn't cause re-render)
    saveOffersToSession(updatedOffers);

    // Update React state immediately (StableNumberInput prevents calls while focused)
    // Since StableNumberInput only calls onChange on blur, this is safe
    setOffers(updatedOffers);
  }, [offers]);

  // Update offer cost data for the new offer details step
  const updateOfferCostData = (offerId: string, costData: OfferCostData) => {
    // Ensure offer details data is initialized
    if (offerDetailsData.offerCostData.length === 0 && offers.length > 0) {
      initializeOfferDetailsData();
      return;
    }

    // Check if the offer data exists, if not, add it
    const existingOfferIndex = offerDetailsData.offerCostData.findIndex(data => data.offerId === offerId);
    let updatedOfferCostData;

    if (existingOfferIndex >= 0) {
      // Update existing offer data
      updatedOfferCostData = offerDetailsData.offerCostData.map(data =>
        data.offerId === offerId ? { ...costData, lastUpdated: new Date() } : data
      );
    } else {
      // Add new offer data
      updatedOfferCostData = [...offerDetailsData.offerCostData, { ...costData, lastUpdated: new Date() }];
    }

    const updatedOfferDetailsData = {
      ...offerDetailsData,
      offerCostData: updatedOfferCostData,
    };

    // Recalculate completion percentage
    updatedOfferDetailsData.completionPercentage = calculateCompletionPercentage(updatedOfferDetailsData);
    updatedOfferDetailsData.isStepComplete = updatedOfferDetailsData.completionPercentage === 100;

    setOfferDetailsData(updatedOfferDetailsData);
    saveOfferDetailsToSession(updatedOfferDetailsData);

    // Save to database if we have a tender ID
    if (tenderId) {
      saveTenderWorkflowState(
        tenderId,
        WORKFLOW_STEPS.DETAILS,
        { offerDetailsData: updatedOfferDetailsData },
        updatedOfferDetailsData.isStepComplete
      );
    }


  };

  // Initialize offer details data when offers change
  const initializeOfferDetailsData = () => {
    if (offers.length === 0) {
      setOfferDetailsData({
        offerCostData: [],
        isStepComplete: false,
        completionPercentage: 0,
      });
      return;
    }

    // Ensure offerDetailsData.offerCostData exists before accessing it
    const existingOfferIds = (offerDetailsData.offerCostData || []).map(data => data.offerId);
    const newOfferCostData: OfferCostData[] = [];

    offers.forEach(offer => {
      const existingData = (offerDetailsData.offerCostData || []).find(data => data.offerId === offer.id);

      if (existingData) {
        // Update existing data with current retailer information from offer
        console.log(`🔄 Updating existing cost data for offer: ${offer.id}`);
        console.log(`   - Old Retailer: ${existingData.retailerId} (${existingData.offerName})`);
        console.log(`   - New Retailer: ${offer.retailerId} (${offer.retailerName})`);

        const updatedExistingData = {
          ...existingData,
          offerName: offer.retailerName || 'Unknown Retailer',
          retailerId: offer.retailerId || '',
        };
        newOfferCostData.push(updatedExistingData);
      } else {
        // Create new data for this offer
        const recommendedMonths = offer.recommendedMonths || [];
        const contractTerms = recommendedMonths.length > 0 ? recommendedMonths : [offer.termMonths];
        const contractBreakdowns: { [termMonths: number]: any } = {};

        contractTerms.forEach(term => {
          contractBreakdowns[term] = createDefaultContractData();
        });

        console.log(`🏪 Creating cost data for offer: ${offer.id}`);
        console.log(`   - Retailer ID: ${offer.retailerId || 'EMPTY'}`);
        console.log(`   - Retailer Name: ${offer.retailerName || 'EMPTY'}`);

        newOfferCostData.push({
          offerId: offer.id,
          offerName: offer.retailerName || 'Unknown Retailer',
          retailerId: offer.retailerId || '',
          contractTerms,
          contractBreakdowns,
          isComplete: false,
          lastUpdated: new Date(),
        });
      }
    });

    const updatedOfferDetailsData = {
      offerCostData: newOfferCostData,
      isStepComplete: false,
      completionPercentage: calculateCompletionPercentage({ offerCostData: newOfferCostData, isStepComplete: false, completionPercentage: 0 }),
    };

    setOfferDetailsData(updatedOfferDetailsData);
    saveOfferDetailsToSession(updatedOfferDetailsData);
  };

  // Save offer cost breakdown data to database
  const saveOfferCostBreakdownsToDatabase = async () => {
    if (!offerDetailsData.offerCostData || offerDetailsData.offerCostData.length === 0) {
      console.log("⚠️ No cost breakdown data to save");
      return;
    }

    try {
      console.log("\n" + "=".repeat(80));
      console.log("💾 STARTING COST BREAKDOWN SAVE PROCESS");
      console.log("=".repeat(80));

      console.log("📊 Cost breakdown data to save:");
      offerDetailsData.offerCostData.forEach((data, index) => {
        console.log(`   ${index + 1}. Offer ID: ${data.offerId}`);
        console.log(`      Contract terms: ${Object.keys(data.contractBreakdowns).join(', ')} months`);
      });

      console.log("\n🔍 Current offers in frontend state:");
      offers.forEach((offer, index) => {
        console.log(`   ${index + 1}. Offer ID: ${offer.id}`);
        console.log(`      Retailer ID: ${offer.retailerId || 'EMPTY'}`);
        console.log(`      Retailer Name: ${offer.retailerName || 'EMPTY'}`);
        console.log(`      Created: ${offer.createdAt || 'Unknown'}`);
      });

      console.log("\n🔍 Cost breakdown data retailer info:");
      offerDetailsData.offerCostData.forEach((data, index) => {
        console.log(`   ${index + 1}. Offer ID: ${data.offerId}`);
        console.log(`      Retailer ID: ${data.retailerId || 'EMPTY'}`);
        console.log(`      Retailer Name: ${data.offerName || 'EMPTY'}`);
      });

      console.log("\n💾 PROCEEDING DIRECTLY TO COST BREAKDOWN SAVE...");
      console.log("(Offers should already exist in database from Step 2)");

      // Save each offer's cost breakdown data
      const savePromises = offerDetailsData.offerCostData.map(async (offerCostData, index) => {
        console.log(`\n💾 [${index + 1}/${offerDetailsData.offerCostData.length}] Processing cost breakdown for offer: ${offerCostData.offerId}`);
        const result = await saveOfferCostBreakdowns(offerCostData);
        if (!result.success) {
          console.error(`❌ Failed to save cost breakdown for offer ${offerCostData.offerId}:`, result.error);
          // throw new Error(`Failed to save cost breakdown for offer ${offerCostData.offerId}: ${result.error}`);
        }
        console.log(`✅ Successfully saved cost breakdown for offer: ${offerCostData.offerId}`);
        return result;
      });

      await Promise.all(savePromises);

      console.log("\n🎉 ALL COST BREAKDOWN DATA SAVED SUCCESSFULLY!");
      console.log("=".repeat(80) + "\n");

      toast({
        title: "Success",
        description: "Cost breakdown data saved to database",
        variant: "default",
      });
    } catch (error) {
      console.error('\n❌ ERROR SAVING COST BREAKDOWN DATA:', error);
      console.log("=".repeat(80) + "\n");
      toast({
        title: "Error",
        description: `Failed to save cost breakdown data: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  // ===== WORKFLOW STATE MANAGEMENT FUNCTIONS =====

  /**
   * Load existing tender workflow state
   */
  const loadTenderWorkflow = useCallback(async (tenderIdToLoad: string, options?: { editMode?: boolean; editStep?: number }) => {
    setIsLoadingTender(true);
    try {
      const result = await loadTenderWorkflowState(tenderIdToLoad);
      if (result.success && result.data) {
        const { currentStep, completedSteps: loadedCompletedSteps, workflowData: loadedWorkflowData, tenderInfo, sites: dbSites } = result.data;

        // Set tender info
        setTenderId(tenderIdToLoad);
        setTenderName(tenderInfo.opportunity_name || '');

        // Load tender details from the tender record (source of truth)
        setTenderDetails({
          type: tenderInfo.type || '',
          account_name: tenderInfo.account_name || '',
          entity_name: tenderInfo.entity_name || '',
          notes: tenderInfo.notes || '',
          abn: tenderInfo.abn || '',
          acn: tenderInfo.acn || '',
          due_at: tenderInfo.due_at || '',
          current_contract_expiry: tenderInfo.current_contract_expiry || '',
          current_meter_provider: tenderInfo.current_meter_provider || '',
          current_retailer: tenderInfo.current_retailer || '',
          dma_provider: tenderInfo.dma_provider || '',
          retailer_comms: tenderInfo.retailer_comms || '',
          create_dma: tenderInfo.create_dma || false,
          request_meter_data: tenderInfo.request_meter_data || false,
          request_bills: tenderInfo.request_bills || false,
          acquire_ced: tenderInfo.acquire_ced || false,
          addr_street: tenderInfo.addr_street || '',
          addr_city: tenderInfo.addr_city || '',
          addr_state: tenderInfo.addr_state || '',
          addr_postcode: tenderInfo.addr_postcode || '',
          addr_country: tenderInfo.addr_country || '',
          sf_opportunity_id: tenderInfo.sf_opportunity_id || '',
        });

        // The tender's contract-term series, if it has one. Restored BEFORE the offers so the
        // label mapping is right the first time a screen renders rather than after a second
        // pass — an unset series leaves {} and every term falls back to the derived year,
        // exactly as before this existed.
        setTermSeriesState(
          (tenderInfo?.extn && typeof tenderInfo.extn === 'object'
            && tenderInfo.extn.term_series && typeof tenderInfo.extn.term_series === 'object')
            ? tenderInfo.extn.term_series
            : {},
        );

        // Load ALL step data from workflow_data and database

        // Step 1: Client & Sites
        if (loadedWorkflowData.step1) {
          if (loadedWorkflowData.step1.clientData) {
            setClientData(loadedWorkflowData.step1.clientData);
          }
          if (loadedWorkflowData.step1.selectedClientId) {
            setSelectedClientId(loadedWorkflowData.step1.selectedClientId);
          }
          if (loadedWorkflowData.step1.tenderName) {
            setTenderName(loadedWorkflowData.step1.tenderName);
          }
          if (loadedWorkflowData.step1.tenderDetails) {
            setTenderDetails(prev => ({
              ...prev,
              ...loadedWorkflowData.step1!.tenderDetails,
            }));
          }
        }

        // US-032: Load sites from database (tender_site table) instead of workflow_data
        if (dbSites && dbSites.length > 0) {
          console.log("✅ Loaded sites from database:", dbSites.length);
          const mappedDbSites: Site[] = dbSites.map((s: any) => ({
            id: s.id,
            siteId: s.site_id,
            site_id: s.site_id,
            // name/nmi are nullable in the DB, so default them like every other string
            // field here — downstream render code calls .trim() on them.
            name: s.name || '',
            nmi: s.nmi || '',
            address: {
              street: s.addr_street || '',
              city: s.addr_city || '',
              state: s.addr_state || '',
              postcode: s.addr_postcode || '',
              country: s.addr_country || 'Australia',
            },
            annualKwhPeak: s.annual_kwh_peak,
            annualKwhOffpeak: s.annual_kwh_offpeak,
            annualKwhShoulder: s.annual_kwh_shoulder,
            dailyCharges: s.daily_charges || 0,
            nonEnergyCharges: s.non_energy_charges || 0,
            peakDemandKw: s.peak_demand_kw || undefined,
            peakDemandKva: s.peak_demand_kva || undefined,
            currentContractRates: s.current_contract_rates || undefined,
            greenPower: s.green_power || false,
            documentFilePaths: s.document_file_paths || s.site?.document_file_paths || [],
            retailerNotes: s.retailer_notes || s.site?.retailer_notes || '',
            abn: s.abn,
            acn: s.acn,
            dueAt: s.due_at ? new Date(s.due_at) : undefined,
            currentContractExpiry: s.current_contract_expiry ? new Date(s.current_contract_expiry) : undefined,
            currentMeterProvider: s.current_meter_provider,
            currentRetailer: s.current_retailer,
            dmaProvider: s.dma_provider,
            retailerComms: s.retailer_comms,
            createDma: s.create_dma || false,
            requestMeterData: s.request_meter_data || false,
            requestBills: s.request_bills || false,
            acquireCed: s.acquire_ced || false,
            energyDetails: {
              offerExpiry: '',
              rateType: 'fixed',
              peakRate: 0,
              offpeakRate: 0,
              shoulderRate: 0,
              cPerKwh: 0,
              nonEnergyCharges: 0,
            },
            extn: s.extn || {},
            orgId: s.org_id,
            createdAt: s.created_at ? new Date(s.created_at) : new Date(),
            isExpanded: false,
            isNew: false,
            isValid: true,
          }));
          setSites(mappedDbSites);
        } else if (loadedWorkflowData.step1?.sites) {
          // Fallback to workflow data for backwards compatibility
          console.log("⚠️ No sites in database, using workflow data as fallback");
          const fallbackSites: Site[] = (loadedWorkflowData.step1.sites as any[]).map((s: any) => ({
            ...s,
            address: s.address || { street: '', city: '', state: '', postcode: '', country: 'Australia' },
          }));
          setSites(fallbackSites);
        }

        // Step 2: Retailer Communication - No specific state to load

        // Step 3: Email Processing - No specific state to load

        // Step 4: Offers - Load from database instead of workflow data
        // step4.offers is canonical (matches WORKFLOW_STEPS.OFFERS); step2.offers is the
        // documented legacy fallback for tenders saved before that numbering was fixed.
        console.log("🔍 Loading offers from database for tender:", tenderIdToLoad);
        const offersResult = await getOffers(tenderIdToLoad);
        if (offersResult.success && offersResult.offers && offersResult.offers.length > 0) {
          console.log("✅ Loaded offers from database:", offersResult.offers.length);
          setOffersWithPersistence(offersResult.offers);
        } else if (loadedWorkflowData.step4?.offers || loadedWorkflowData.step2?.offers) {
          console.log("⚠️ No offers in database, using workflow data as fallback");
          setOffersWithPersistence(loadedWorkflowData.step4?.offers || loadedWorkflowData.step2?.offers);
        } else {
          console.log("ℹ️ No offers found in database or workflow data");
        }

        // Step 5: Compare & Select - No specific state to load

        // Step 6: Recommended Offers (uses offers array)
        // No additional state needed

        // Step 7: Template Selection
        // step7 is canonical (matches WORKFLOW_STEPS.TEMPLATE); step5 is the documented
        // legacy fallback for tenders saved before that numbering was fixed.
        const templateStep = loadedWorkflowData.step7 || loadedWorkflowData.step5;
        // Fall back to a flat workflow_data.selectedTemplate — an older save path (the
        // PUT /tenders/:id/step route) merges stepData straight into workflow_data's root
        // instead of nesting it under step7, so some tenders have it there instead. Without
        // this fallback, selectedTemplate silently restores empty and every Generate button
        // that guards on `!selectedTemplate` (Combined and each per-site Separate button)
        // does nothing when clicked — no error, just a dead click.
        const restoredTemplate = templateStep?.selectedTemplate || (loadedWorkflowData as any).selectedTemplate;
        if (restoredTemplate) {
          setSelectedTemplate(restoredTemplate);
        }
        if (templateStep?.referenceDocument) {
          setReferenceDocument(templateStep.referenceDocument);
        }

        // Step 8: Offer Details
        // step8 is canonical (matches WORKFLOW_STEPS.DETAILS); step6 is the documented
        // legacy fallback for tenders saved before that numbering was fixed.
        const detailsStep = loadedWorkflowData.step8 || loadedWorkflowData.step6;
        if (detailsStep?.offerDetailsData) {
          setOfferDetailsData(detailsStep.offerDetailsData);
        }

        // Step 9: Document Overview (uses all previous data)
        // No additional state needed

        setWorkflowData(loadedWorkflowData);

        if (options?.editMode) {
          // Edit & Regenerate flow: jump to requested step with all steps unlocked
          const editStep = options.editStep ?? 9;
          setCurrentStep(editStep);
          setCompletedSteps([1, 2, 3, 4, 5, 6, 7, 8]);
          toast({
            title: "Edit Mode",
            description: "All steps unlocked. Edit any step then regenerate.",
          });
          return { success: true, currentStep: editStep };
        }

        // Normal resume: set step from DB
        let targetStep = currentStep;
        const currentStepKey = `step${currentStep}` as keyof typeof loadedWorkflowData;
        if (loadedWorkflowData[currentStepKey]?.completed && currentStep < 9) {
          targetStep = currentStep + 1;
        }

        setCurrentStep(targetStep);

        // US-032: Set completed steps from database
        if (loadedCompletedSteps && Array.isArray(loadedCompletedSteps)) {
          setCompletedSteps(loadedCompletedSteps);
        }

        // US-032: Show toast notification
        toast({
          title: "Tender Resumed",
          description: `Resuming from Step ${targetStep}`,
        });

        return { success: true, currentStep: targetStep };
      } else {
        throw new Error(result.error || 'Failed to load tender');
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to load tender: ${error.message}`,
        variant: "destructive",
      });
      return { success: false, error: error.message };
    } finally {
      setIsLoadingTender(false);
    }
  }, [setTenderId, setTenderName, setClientData, setSites, setSelectedClientId, setOffers, setSelectedTemplate, setOfferDetailsData, setCurrentStep, setWorkflowData, setTenderDetails, toast]);

  /**
   * Jump to a specific step with all steps marked complete (edit-and-regenerate flow)
   */
  const enterEditMode = useCallback((step: number = 8) => {
    setCurrentStep(step);
    setCompletedSteps([1, 2, 3, 4, 5, 6, 7, 8]);
  }, []);

  /**
   * Save current step data to database
   */
  const saveCurrentStepData = useCallback(async (stepNumber: number, stepData: any, isCompleted: boolean = false) => {
    if (!tenderId) return;

    try {
      await saveTenderWorkflowState(tenderId, stepNumber as any, stepData, isCompleted);
    } catch (error) {
      // Silent error handling
    }
  }, [tenderId]);

  /**
   * Get current step data for saving to database
   */
  const getCurrentStepData = useCallback((stepNumber: number) => {
    switch (stepNumber) {
      case 1: // Client & Sites
        return {
          clientData,
          sites,
          tenderName,
          selectedClientId,
          tenderDetails,
          completed: true
        };
      case 2: // Retailer Communication
        return {
          completed: true
        };
      case 3: // Email Processing
        return {
          completed: true
        };
      case 4: // Offers
        return {
          offers,
          completed: true
        };
      case 5: // Compare & Select
        return {
          completed: true
        };
      case 6: // Recommended Offers
        return {
          recommendedOffers: offers.filter(offer => offer.isRecommended || offer.recommendedMonths?.length > 0),
          completed: true
        };
      case 7: // Template Selection
        return {
          selectedTemplate,
          referenceDocument,
          completed: true
        };
      case 8: // Offer Details
        return {
          offerDetailsData,
          completed: true
        };
      case 9: // Document Overview
        return {
          documentData: {
            clientData,
            sites,
            offers,
            selectedTemplate,
            offerDetailsData
          },
          completed: true
        };
      default:
        return null;
    }
  }, [clientData, sites, tenderName, selectedClientId, offers, selectedTemplate, referenceDocument, offerDetailsData]);

  /**
   * Update current step in database when user navigates - US-031
   * Now uses REST API endpoint instead of direct service call
   */
  const updateCurrentStep = useCallback(async (newStep: number, markStepComplete: boolean = false) => {
    if (!tenderId) {
      console.log('⚠️ No tenderId, skipping step update');
      return;
    }

    try {
      // Update completed steps if marking this step as complete
      let updatedCompletedSteps = [...completedSteps];
      if (markStepComplete && !completedSteps.includes(newStep - 1)) {
        updatedCompletedSteps = [...completedSteps, newStep - 1].sort();
        setCompletedSteps(updatedCompletedSteps);
      }

      // Get step data for the PREVIOUS step (the one being completed)
      const stepData = markStepComplete ? getCurrentStepData(newStep - 1) : undefined;

      // Call REST API endpoint (US-029)
      const result = await updateTenderStep(
        tenderId,
        newStep,
        updatedCompletedSteps,
        stepData
      );

      if (result.success) {
        console.log(`✅ Updated tender step to ${newStep}`, {
          completed: markStepComplete,
          completedSteps: updatedCompletedSteps
        });
      } else {
        console.error('❌ Failed to update tender step:', result.error);
      }
    } catch (error) {
      console.error('❌ Error updating tender step:', error);
    }
  }, [tenderId, completedSteps, getCurrentStepData]);

  /**
   * Create new tender and initialize workflow
   */
  const createTenderWorkflow = useCallback(async (clientId: string, tenderName: string) => {
    try {
      const result = await createNewTender(clientId, tenderName, {
        step1: {
          clientData,
          sites,
          tenderName,
          selectedClientId: clientId,
          completed: false
        }
      });

      if (result.success && result.tenderId) {
        setTenderId(result.tenderId);
        return { success: true, tenderId: result.tenderId };
      } else {
        throw new Error(result.error || 'Failed to create tender');
      }
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }, [clientData, sites, setTenderId]);

  // Handle budget range changes
  const handleBudgetRangeChange = (
    field: "budgetMin" | "budgetMax",
    value: string
  ) => {
    const numValue = value === "" ? undefined : parseFloat(value);
    setProjectInfo((prev) => ({ ...prev, [field]: numValue }));
  };

  // Handle prompt selection
  const handlePromptSelect = (promptId: string) => {
    const prompt = promptTemplates.find((p) => p.id === promptId);
    if (prompt) {
      setPromptData({
        id: prompt.id,
        name: prompt.name,
        description: prompt.description,
        content: prompt.content,
        variables: prompt.variables,
      });
      setSelectedPrompt(prompt);
      setIsNewPrompt(false);
      setPromptSearchOpen(false);
    }
  };

  // Handle prompt input changes
  const handlePromptInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setPromptData((prev) => ({ ...prev, [name]: value }));

    // If user is typing a new name, mark as new prompt
    if (name === "name") {
      setIsNewPrompt(true);
      setSelectedPrompt(null);
    }
  };

  // Handle AI prompt changes
  const handlePromptChange = (field: keyof AiPrompts, value: string) => {
    setAiPrompts((prev) => ({ ...prev, [field]: value }));
  };

  // Save client data
  const saveClient = async () => {
    setIsSavingClient(true);
    try {
      if (isNewClient && clientData.name) {
        // Get user's organizations
        const organisationIds =
          await enhancedClientService.getUserOrganizations();
        if (organisationIds.length === 0) {
          throw new Error(
            "You must be a member of an organization to create contacts"
          );
        }

        // Create new client in CRM
        const newClient = await enhancedClientService.createCrmContact({
          name: clientData.name,
          email: clientData.email || undefined,
          phone: clientData.phone || undefined,
          industry: clientData.industry || undefined,
        });

        // Reload clients to get the updated list
        const updatedClients = await loadClients();

        // Select the newly created client
        setSelectedClient(newClient);
        setIsNewClient(false);

        toast({
          title: "Contact saved successfully",
          description:
            "Contact information has been saved to CRM and can now be selected",
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save contact";
      console.error("Error saving contact:", error);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSavingClient(false);
    }
  };

  // Save prompt template data
  const savePromptTemplate = async () => {
    setIsSavingPrompt(true);
    try {
      // Mock API call - replace with actual implementation
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Add the new prompt to the list if it's truly new
      if (
        isNewPrompt &&
        !promptTemplates.find((p) => p.name === promptData.name)
      ) {
        const newPrompt: PromptTemplate = {
          ...promptData,
          id: Date.now().toString(),
          created_at: new Date().toISOString(),
        };
        setPromptTemplates((prev) => [...prev, newPrompt]);
        setSelectedPrompt(newPrompt);
      }

      setIsNewPrompt(false);

      toast({
        title: "Prompt template saved successfully",
        description: "Prompt template has been saved",
      });
    } catch (error) {
      console.error("Error saving prompt template:", error);
      // Removed toast error to prevent UI spam
    } finally {
      setIsSavingPrompt(false);
    }
  };

  // Navigation handlers
  // Validation functions for each step, mapped to the current 9-step flow
  // (ClientInfo -> RetailerComm -> EmailProcessing -> Offers -> CompareAndSelect ->
  // RecommendedOffer -> TemplateSelection -> OfferDetails -> DocumentOverview).
  const validateStep = (
    step: number
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    switch (step) {
      case 1: // Client Info Step
        if (!clientData.name?.trim()) {
          errors.push("Client name is required");
        }
        if (!sites || sites.length === 0) {
          errors.push("At least one site is required");
        } else {
          sites.forEach((site, index) => {
            if (!site.name?.trim()) {
              errors.push(`Site ${index + 1}: Name is required`);
            }
            if (!site.nmi?.trim()) {
              errors.push(`Site ${index + 1}: NMI is required`);
            }
          });
        }
        break;

      case 4: { // Offers Step
        if (!offers || offers.length === 0) {
          errors.push("At least one offer is required");
        }
        break;
      }

      case 5: { // Compare & Select Step
        if (offers.length === 0) {
          errors.push("Please add offers before comparing");
        }
        break;
      }

      case 6: { // Recommended Offer Step
        const hasRecommendedOffers = offers.some(offer =>
          offer.recommendedMonths && offer.recommendedMonths.length > 0
        );
        if (!hasRecommendedOffers) {
          errors.push("Please select a recommended offer");
        }
        break;
      }

      case 7: { // Template Selection Step
        if (!selectedTemplate) {
          errors.push("Please select a document template");
        }
        break;
      }

      case 9: { // Document Overview Step - aggregate the steps that carry real requirements
        errors.push(...validateStep(1).errors);
        errors.push(...validateStep(4).errors);
        errors.push(...validateStep(5).errors);
        errors.push(...validateStep(6).errors);
        errors.push(...validateStep(7).errors);
        break;
      }

      // Steps 2 (Retailer Communication), 3 (Email Processing), and 8 (Offer Details)
      // have no mandatory fields.
    }

    return { isValid: errors.length === 0, errors };
  };

  // Check if current step is valid for enabling/disabling Next button
  const isCurrentStepValid = () => {
    const validation = validateStep(currentStep);
    return validation.isValid;
  };

  const handleNext = async () => {
    const validation = validateStep(currentStep);

    if (!validation.isValid) {
      // Show validation errors
      validation.errors.forEach((error) => {
        toast({
          title: "Validation Error",
          description: error,
          variant: "destructive",
        });
      });
      return;
    }

    // Check if we should skip template selection step for existing proposals
    const shouldSkipTemplateStep = preSelectedTemplate?.type === "existing_proposal";

    // Save current step data before moving to next step
    if (tenderId) {
      const stepData = getCurrentStepData(currentStep);
      if (stepData) {
        saveCurrentStepData(currentStep, stepData, true);

        // If moving from step 8 (Offer Details), also save cost breakdown data to database
        if (currentStep === 8) {
          await saveOfferCostBreakdownsToDatabase();
        }
      }
    }

    if (currentStep < 9) {
      let nextStep = currentStep + 1;

      // Skip directly to overview for existing proposals
      if (currentStep === 1 && shouldSkipTemplateStep) {
        nextStep = 2; // Skip directly to overview (no requirements step)
      }

      setCurrentStep(nextStep);
      // Update current step in database and mark previous step as complete - US-031
      updateCurrentStep(nextStep, true);
    }
  };

  // US-033: Check if navigation to a specific step is allowed
  const canNavigateToStep = useCallback((targetStep: number): boolean => {
    // Always allow backward navigation — never restrict going back
    if (targetStep < currentStep) {
      return true;
    }
    // Can always stay on current step
    if (targetStep === currentStep) {
      return true;
    }
    // Forward navigation only if every step between here and the target is completed.
    // handleNext moves one step at a time, so this is identical to the old
    // `completedSteps.includes(currentStep)` check for targetStep === currentStep + 1;
    // it only adds the guard that a stepper click cannot skip over unfinished steps.
    for (let step = currentStep; step < targetStep; step += 1) {
      if (!completedSteps.includes(step)) return false;
    }
    return true;
  }, [currentStep, completedSteps]);

  const handleBack = () => {
    if (currentStep > 1) {
      let prevStep = currentStep - 1;

      // Check if we should skip template selection step for existing proposals
      const shouldSkipTemplateStep = preSelectedTemplate?.type === "existing_proposal";

      // Skip step 6 (template selection) when going back if we have an existing proposal
      if (currentStep === 8 && shouldSkipTemplateStep) {
        prevStep = 1; // Skip back to client info
      }

      // US-033: Validate backward navigation (allow to any completed step)
      if (!canNavigateToStep(prevStep)) {
        toast({
          title: "Navigation Restricted",
          description: "You can only navigate back to completed steps.",
          variant: "destructive",
        });
        return;
      }

      setCurrentStep(prevStep);
      // Update current step in database (going back, don't mark as complete) - US-031
      updateCurrentStep(prevStep, false);
    }
  };



  /**
   * Flush handlers a step registers so its in-progress edits survive being navigated away from.
   *
   * ⚠️ Needed because saveCurrentStepData does NOT persist every step. Step 3 returns only
   * `{ completed: true }` — the offer review edits live in EmailProcessingStep's child and reach
   * the database solely through its own Next button calling flushCurrentEdit. Jumping straight
   * from the stepper skips that button, so without this an in-progress review is lost with
   * nothing to recover it from.
   *
   * A ref, not state: registering must not re-render, and goToStep needs the CURRENT handler at
   * the moment of navigation rather than one captured in a stale closure.
   */
  const stepFlushHandlers = useRef<Map<number, () => void | Promise<void>>>(new Map());

  const registerStepFlush = useCallback((step: number, flush: () => void | Promise<void>) => {
    stepFlushHandlers.current.set(step, flush);
    return () => {
      // Only remove our own handler — a remount may already have registered a newer one.
      if (stepFlushHandlers.current.get(step) === flush) stepFlushHandlers.current.delete(step);
    };
  }, []);

  /**
   * Jump straight to a step from the stepper, instead of pressing Back repeatedly.
   *
   * Does everything the Next button does, in the same order, so a jump can never be a cheaper
   * way to leave a step than the button is:
   *   1. refuse if canNavigateToStep says no (backward always fine; forward only to a completed step)
   *   2. flush the leaving step's own in-progress edits
   *   3. persist what that step contributes, exactly as handleNext does
   *   4. move, marking complete only when going forward — handleBack's rule
   *
   * A failed flush stops the navigation. Losing the edit silently is worse than staying put.
   */
  const stepNavigationInFlight = useRef(false);

  const goToStep = useCallback(async (targetStep: number): Promise<void> => {
    if (targetStep === currentStep) return;
    // A flush can be slow (step 4 saves offers AND recalculates). Two overlapping jumps would
    // interleave their saves and race to setCurrentStep, so the second click is ignored.
    if (stepNavigationInFlight.current) return;

    if (!canNavigateToStep(targetStep)) {
      toast({
        title: "Navigation Restricted",
        description: "Finish the current step before jumping ahead.",
        variant: "destructive",
      });
      return;
    }

    const movingForward = targetStep > currentStep;
    stepNavigationInFlight.current = true;

    try {
      // Flush in BOTH directions — an in-progress edit must survive leaving the step no matter
      // which way the user goes. This is the one thing neither handleNext nor handleBack does,
      // and the only reason this function is not just a call to one of them.
      const flush = stepFlushHandlers.current.get(currentStep);
      if (flush) {
        try {
          await flush();
        } catch (error) {
          console.error(`Could not save step ${currentStep} before navigating:`, error);
          toast({
            title: "Unsaved changes",
            description: "This step could not be saved, so you have not been moved. Try again.",
            variant: "destructive",
          });
          return;
        }
      }

      if (movingForward) {
        // Mirror handleNext exactly. It validates before leaving, and step 8 has a second
        // database write beyond the workflow snapshot; skipping either made a stepper jump a
        // cheaper way out of a step than the Next button, which is never correct.
        const validation = validateStep(currentStep);
        if (!validation.isValid) {
          validation.errors.forEach((error) => {
            toast({ title: "Validation Error", description: error, variant: "destructive" });
          });
          return;
        }

        if (tenderId) {
          const stepData = getCurrentStepData(currentStep);
          if (stepData) {
            await saveCurrentStepData(currentStep, stepData, true);
            if (currentStep === 8) await saveOfferCostBreakdownsToDatabase();
          }
        }
      }
      // Backward: write NOTHING, exactly like handleBack.
      //
      // Writing the leaving step's snapshot here was wrong in two ways. First,
      // saveTenderWorkflowState stores `completed: isStepCompleted`, so a backward jump stamped
      // `completed: false` onto a step that was genuinely finished. Second, loadTenderWorkflow
      // still reads legacy aliases — `step7 || step5` for the template and `step8 || step6` for
      // offer details — so merely CREATING a step5/step6 key (which handleBack never does) makes
      // those fallbacks resolve to an object that has no selectedTemplate/offerDetailsData, and
      // the real values stop hydrating on the next load.
      setCurrentStep(targetStep);
      // updateCurrentStep marks `newStep - 1` complete and saves THAT step's data — an
      // assumption baked in by handleNext, which only ever moves one step. A 3 -> 6 jump would
      // therefore mark step 5 complete and write step 5's payload while the user was on step 3.
      // Multi-step forward jumps are already gated on those steps being complete (and the
      // saveCurrentStepData above records the real one), so there is nothing left to mark.
      updateCurrentStep(targetStep, movingForward && targetStep === currentStep + 1);
    } finally {
      stepNavigationInFlight.current = false;
    }
  }, [currentStep, canNavigateToStep, tenderId, getCurrentStepData, saveCurrentStepData, updateCurrentStep, validateStep, saveOfferCostBreakdownsToDatabase, toast]);

  const handleRemoveReference = () => {
    setReferenceDocument(null);
    // Reset template to standard when reference is removed
    setSelectedTemplate("");
  };

  const handleTemplateSelect = async (templateId: string) => {
    console.log("🎯 Template selection started:", {
      templateId,
      currentTemplate: selectedTemplate,
    });

    setSelectedTemplate(templateId);

    // If it's an existing document (starts with 'doc_' or 'existing_'), load the document data
    if (templateId.startsWith("doc_") || templateId.startsWith("existing_")) {
      try {
        const documentId = templateId.startsWith("existing_")
          ? templateId.replace("existing_", "")
          : templateId.replace("doc_", "");
        console.log("📄 Loading existing document/proposal:", documentId);

        // Import supabase client to load document data directly
        const { supabase } = await import("../../../foundation/altzero_base/base/utils/supabaseClient");

        // Fetch document from scopingai_documents table
        const { data, error } = await supabase
          .from("scopingai_documents")
          .select(`
            id,
            title,
            content,
            metadata,
            crm_companies (
              name
            )
          `)
          .eq("id", documentId)
          .single();

        if (error) {
          console.error("❌ Error loading document:", error);
          return;
        }

        if (data) {
          console.log("✅ Document loaded successfully:", {
            id: data.id,
            title: data.title,
            hasContent: !!data.content,
          });

          // Parse sections from content
          const sections = data.content?.sections || [];
          const markdownContent = data.content?.markdown || "";

          setReferenceDocument({
            id: data.id,
            title: data.title,
            client: (data.crm_companies as any)?.[0]?.name || (data.crm_companies as any)?.name || "Unknown Client",
            date: new Date().toISOString(),
            sections: sections,
            content: {
              overview: markdownContent.substring(0, 200) + "...",
              objectives: [],
              fullContent: markdownContent,
              parsedSections: sections.map((s: any) => ({
                title: s.title,
                content: s.content,
                description: s.description || ""
              }))
            },
            type: "custom"
          });
        } else {
          console.error("❌ Document not found:", documentId);
        }
      } catch (error) {
        console.error("❌ Error loading existing document/proposal:", error);
      }
    } else {
      // For regular templates, clear the reference document
      console.log("🧹 Clearing reference document for regular template");
      setReferenceDocument(null);
    }
  };

  // Auto-select template when preSelectedTemplate is available
  useEffect(() => {
    if (preSelectedTemplate?.type === "existing_proposal" && preSelectedTemplate.id) {
      console.log("🎯 Auto-selecting existing proposal template:", preSelectedTemplate);
      handleTemplateSelect(preSelectedTemplate.id);
    }
  }, [preSelectedTemplate]);

  // Document ID reference for real-time updates
  const documentIdRef = useRef<string | null>(null);

  // Generation handler
  const handleGenerate = async (preserveOriginalContent: boolean) => {
    console.log(
      "handleGenerate called with preserveOriginalContent:",
      preserveOriginalContent
    );
    console.log("Current project info:", projectInfo);
    console.log("Current client data:", clientData);
    console.log("Current selected template:", selectedTemplate);
    console.log("Current reference document:", referenceDocument);
    console.log("Selected knowledge documents:", selectedKnowledgeDocuments);
    console.log("Document requirements:", documentRequirements);

    // Enhanced debugging for existing documents/proposals
    if (selectedTemplate.startsWith("doc_") || selectedTemplate.startsWith("existing_")) {
      console.log("🔍 Existing document/proposal generation details:", {
        templateId: selectedTemplate,
        hasReferenceDocument: !!referenceDocument,
        referenceDocumentId: referenceDocument?.id,
        referenceDocumentTitle: referenceDocument?.title,
        referenceDocumentSections: referenceDocument?.sections?.length || 0,
        referenceDocumentType: referenceDocument?.type,
      });
    }

    // Validate required fields before starting generation
    if (!projectInfo.title || projectInfo.title.trim() === "") {
      console.log("Project title is required");
      // Removed toast error to prevent UI spam
      return;
    }

    if (!clientData.name || clientData.name.trim() === "") {
      console.log("Client information is required");
      // Removed toast error to prevent UI spam
      return;
    }

    if (
      requirementsOption === "requirements" &&
      (!requirementsText || requirementsText.trim() === "")
    ) {
      console.log("Requirements are required");
      // Removed toast error to prevent UI spam
      return;
    }

    if (
      requirementsOption === "knowledge" &&
      (!selectedKnowledgeDocuments || selectedKnowledgeDocuments.length === 0)
    ) {
      console.log("Knowledge base documents are required");
      // Removed toast error to prevent UI spam
      return;
    }

    setIsGenerating(true);
    setGenerationStatus("Preparing document generation...");
    resetProgressTracking(); // Reset progress tracking for new generation
    setHasRedirected(false); // Reset redirect flag for new generation

    try {
      // Prepare document data with all real information
      const documentData = prepareDocumentData(
        projectInfo,
        clientData,
        selectedClient,
        selectedTemplate,
        referenceDocument,
        requirementsOption,
        requirementsText,
        selectedKnowledgeDocuments,
        documentRequirements,
        aiPrompts,
        templates,
        user, // Pass user for clientId resolution
        documentLogo, // Pass logo data
        documentBranding, // Pass branding data
        tenderId // Pass tender ID for cost data lookup
      );

      console.log(
        "Document title being sent for generation:",
        projectInfo.title
      );
      console.log("Sending document data for generation:", documentData);
      console.log(
        "Knowledge base documents included:",
        selectedKnowledgeDocuments
      );
      console.log("Preserve original content:", preserveOriginalContent);

      // Additional validation after data preparation
      if (!documentData.title) {
        throw new Error("Project title is required for document generation");
      }

      if (!documentData.client?.name && !clientData.name) {
        throw new Error(
          "Client information is required for document generation"
        );
      }

      if (requirementsOption === "requirements" && !requirementsText.trim()) {
        throw new Error(
          "Requirements text is required when manual entry is selected"
        );
      }

      if (
        requirementsOption === "knowledge" &&
        (!selectedKnowledgeDocuments || selectedKnowledgeDocuments.length === 0)
      ) {
        throw new Error("Please select at least one knowledge base document");
      }

      // Define callbacks for the streaming events
      const callbacks = {
        onStarted: (data: any) => {
          console.log(
            "🚀 Generation started (real-time progress will override):",
            data
          );

          // Start with initial progress
          updateProgress(5, "Document generation started...");

          // Prioritize server-provided document ID
          const newDocId = data.documentId || data.proposal?.id || data.id;

          if (newDocId && newDocId !== "null" && newDocId !== "undefined") {
            console.log("✅ Using server-provided document ID:", newDocId);
            setDocumentId(newDocId);
          } else {
            const fallbackId = `doc_${Date.now()}`;
            console.warn(
              "⚠️ No server document ID, using fallback:",
              fallbackId
            );
            setDocumentId(fallbackId);
          }
        },
        onProgress: (data: any) => {
          console.log("🔥 REAL-TIME PROGRESS UPDATE:", data);
          console.log("🔥 Progress:", data.progress, "Message:", data.message);

          // Use monotonic progress tracking
          updateProgress(data.progress, data.message || "Processing...");
        },
        onResearch: (data: any) => {
          console.log(
            "📚 Research event (real-time progress will override):",
            data
          );
          // Don't set static progress - let real-time progress handle it
        },
        onSummary: (data: any) => {
          console.log(
            "📋 Summary event (real-time progress will override):",
            data
          );
          // Don't set static progress - let real-time progress handle it
        },
        onSection: (data: any) => {
          console.log(
            "📄 Section event (real-time progress will override):",
            data
          );
          // Don't set static progress - let real-time progress handle it
        },
        onCompleted: (data: any) => {
          console.log(
            "🎉 onCompleted called - checking if this is REALLY the final completion:",
            data
          );

          // STRICT completion check - only proceed if we have actual proposal data
          const hasActualProposal =
            data.proposal &&
            (data.proposal.sections?.length > 0 ||
              data.proposal.summary ||
              data.proposal.content);

          const isDefinitelyComplete =
            hasActualProposal ||
            (data.complete === true && data.status === "completed");

          console.log("🔍 Strict completion check:", {
            hasActualProposal,
            isDefinitelyComplete,
            proposalSections: data.proposal?.sections?.length || 0,
            proposalSummary: !!data.proposal?.summary,
            complete: data.complete,
            status: data.status,
          });

          // 🔍 CRITICAL FIX: Store session data IMMEDIATELY when we have ANY proposal data
          // This ensures session storage works even if completion checks fail in production

          // Store document ID if available, with multiple fallback options (declare outside if block)

          const newDocId =
            // data.proposal?.id ||
            data.documentId || data.id || documentId || `doc_${Date.now()}`;
          console.log("Setting final document ID:", newDocId);
          setDocumentId(newDocId);

          if (data.proposal || data.document || data.sections) {
            console.log(
              "💾 PRIORITY: Storing proposal data in session storage IMMEDIATELY"
            );

            // Store the proposal data in session storage
            try {
              // 🚨 FIXED: Ensure sections have unique IDs and prevent duplication
              const dataWithUniqueIds = { ...data };

              // Get sections from either proposal.sections or sections (but not both)
              const rawSections = dataWithUniqueIds.proposal?.sections || dataWithUniqueIds.sections || [];

              // 🚨 FIXED: Deduplicate sections by title and filter out empty content
              const uniqueSections = rawSections.filter((section: any, index: number, array: any[]) => {
                // Remove duplicates by title
                const firstIndex = array.findIndex((s: any) => s.title === section.title);
                return firstIndex === index;
              }).filter((section: any) => {
                // Only keep sections with actual content
                return section.content && section.content.trim().length > 0;
              });

              const sectionsWithUniqueIds = uniqueSections.map((section: any, index: number) => ({
                ...section,
                id: `section-${index + 1}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              }));

              console.log('🔍 Section processing in AI generation:', {
                originalCount: rawSections.length,
                afterDeduplication: uniqueSections.length,
                withContent: sectionsWithUniqueIds.length,
                duplicatesRemoved: rawSections.length - uniqueSections.length,
                emptyRemoved: uniqueSections.length - sectionsWithUniqueIds.length
              });

              // 🚨 FIXED: Store sections in only ONE place to prevent duplication
              if (dataWithUniqueIds.proposal) {
                dataWithUniqueIds.proposal.sections = sectionsWithUniqueIds;
                // Remove duplicate sections property if it exists
                delete dataWithUniqueIds.sections;
              } else {
                dataWithUniqueIds.sections = sectionsWithUniqueIds;
              }

              sessionStorage.setItem(
                "scopingai_proposal_data",
                JSON.stringify(dataWithUniqueIds)
              );
              console.log(
                "✅ Proposal data stored successfully in session storage with unique section IDs"
              );

              // Also store with the document-specific key for consistency
              if (newDocId) {
                // 🚨 FIXED: Use the already processed sections to prevent re-processing
                console.log('🔧 Using already processed sections for document-specific storage:', {
                  processedCount: sectionsWithUniqueIds.length,
                  sampleIds: sectionsWithUniqueIds.slice(0, 3).map((s: any) => s.id)
                });

                // 🚨 FIXED: Remove duplicate sections property to prevent duplication during save
                const documentSpecificData = {
                  documentData: {
                    title:
                      data.proposal?.title ||
                      projectInfo.title ||
                      "Generated Proposal",
                    client: data.proposal?.clientName || clientData.name || "",
                    type: "Scoping Document",
                    sections: sectionsWithUniqueIds,
                  },
                  // 🚨 REMOVED: sections: sectionsWithUniqueIds, - This was causing duplication
                  overview: "",
                  status: "completed",
                  documentId: newDocId,
                  clientId: selectedClient?.id || clientData.id,
                  startedAt: new Date().toISOString(),
                  completedAt: new Date().toISOString(),
                  preserveOriginalContent: preserveOriginalContent,
                  knowledgeBaseUsed:
                    (selectedKnowledgeDocuments?.length || 0) > 0,
                  knowledgeDocumentCount:
                    selectedKnowledgeDocuments?.length || 0,
                };

                sessionStorage.setItem(
                  `document_${newDocId}`,
                  JSON.stringify(documentSpecificData)
                );
                console.log(
                  `✅ Document-specific data stored: document_${newDocId}`
                );
              }
            } catch (error) {
              console.error("❌ Failed to store session data:", error);
            }
          }

          // Now do the completion checks (but don't let them prevent session storage)
          if (!isDefinitelyComplete) {
            console.log(
              "⚠️ onCompleted: Not fully complete yet, but session data saved..."
            );
            // Don't return here - let the process continue for UI updates
          }

          // Prevent multiple redirects
          if (hasRedirected) {
            console.log("❌ onCompleted: Already redirected, ignoring...");
            return;
          }

          console.log(
            "✅ onCompleted: Processing completion (session data already saved)!"
          );

          // Mark as redirected to prevent multiple redirects
          setHasRedirected(true);

          // Set progress to 100% ONLY when we have actual content
          setGenerationProgress(100);

          // Calculate actual section count with better fallback logic
          const sectionCount =
            data.proposal?.sections?.length ||
            data.sections?.length ||
            (data.proposal?.content ? 1 : 0) ||
            0;

          setGenerationStatus(
            `Document generated ! Click to view your proposal.`
          );

          console.log(
            "✅ FINAL COMPLETION with content - AUTO REDIRECTING TO PROPOSALS PAGE!"
          );

          // Update state to show completion and close dialog
          setIsLoading(false);
          setIsGenerating(false); // Close the dialog

          console.log(
            "🔄 AUTO REDIRECT - Redirecting to proposals page after generation"
          );

          // Store the final URL for manual navigation
          const finalDocId =
            newDocId && newDocId !== "null" && newDocId !== "undefined"
              ? newDocId
              : `doc_${Date.now()}`;

          const titleParam = projectInfo.title
            ? `&title=${encodeURIComponent(projectInfo.title)}`
            : "";
          const preserveParam = preserveOriginalContent
            ? `&preserveOriginal=true`
            : "";
          const knowledgeParam =
            selectedKnowledgeDocuments.length > 0
              ? `&knowledgeUsed=true&knowledgeCount=${selectedKnowledgeDocuments.length}`
              : "";

          const finalUrl = `/tender/documents/generated?id=${finalDocId}${titleParam}${preserveParam}${knowledgeParam}`;
          console.log("🔗 Final URL ready for manual navigation:", finalUrl);

          // Store URL in sessionStorage for manual access
          sessionStorage.setItem("scopingai_generated_url", finalUrl);

          // 🚨 FIXED: Auto-redirect to AI generated page instead of proposals page
          setTimeout(() => {
            console.log("🔄 Auto-redirecting to AI generated document page");
            window.location.href = finalUrl;
          }, 2000);
        },
        onEnd: (data: any) => {
          console.log(
            "🏁 Generation ended (real-time progress handled completion):",
            data
          );
          // Don't set static progress - real-time progress already handled 100%

          // Get the document ID with multiple fallback options, prioritizing the reference
          const docId =
            documentIdRef.current ||
            data.documentId ||
            data.proposal?.id ||
            data.id ||
            documentId ||
            `doc_${Date.now()}`;
          console.log("Final docId for navigation:", docId);
          console.log("Document ID from reference:", documentIdRef.current);

          // Ensure we have a valid document ID
          if (!docId || docId === "null" || docId === "undefined") {
            const fallbackId = `doc_${Date.now()}`;
            console.warn(
              "No valid document ID found, using fallback:",
              fallbackId
            );
            setDocumentId(fallbackId);
          }

          // Store logo and branding data in session storage for DocumentContext
          if (documentLogo || documentBranding) {
            const sessionData = {
              logo: documentLogo,
              branding: documentBranding,
              timestamp: Date.now(),
            };
            sessionStorage.setItem(
              `document_${docId}_branding`,
              JSON.stringify(sessionData)
            );
            console.log(
              "💾 Stored logo and branding data in session storage:",
              sessionData
            );
          }

          console.log(
            "🏁 onEnd called - but NOT redirecting from here anymore!"
          );
          console.log("🏁 onEnd data:", data);

          // NEVER redirect from onEnd - only from onCompleted
          // Just update the document ID if available
          setIsLoading(false);

          console.log(
            "⏳ onEnd: Waiting for onCompleted to handle redirect..."
          );
        },
        onError: (error: any) => {
          console.error("❌ Generation error:", error);
          setIsLoading(false);
          setIsGenerating(false);
          setGenerationProgress(0);

          // Provide specific error messages based on error type
          let errorMessage =
            "There was an error generating your document. Please try again.";
          let errorTitle = "Generation Failed";

          if (error.type === "network_error") {
            errorTitle = "Connection Lost";
            errorMessage =
              "Please check your internet connection and try again.";
            setGenerationStatus("Connection lost - please retry");
          } else if (error.type === "timeout_error") {
            errorTitle = "Request Timeout";
            errorMessage =
              "The generation is taking longer than expected. Please try again.";
            setGenerationStatus("Request timed out - please retry");
          } else {
            setGenerationStatus("Generation failed - please retry");
            errorMessage = error?.message || errorMessage;
          }

          toast({
            title: errorTitle,
            description: errorMessage,
            variant: "destructive",
          });
        },
      };

      // Start the streaming process
      await generateDocument(
        documentData,
        preserveOriginalContent,
        callbacks,
        eventSourceRef,
        documentIdRef
      );
    } catch (error) {
      console.error("Error in handleGenerate:", error);
      setIsLoading(false);
      setIsGenerating(false);
      setGenerationStatus("Generation failed");
      setGenerationProgress(0);

      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      toast({
        title: "Generation Failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  // Combine all values and functions to provide via context
  const contextValue: DocumentFormContextType = {
    // State
    currentStep,
    isGenerating,
    isLoading,
    isSavingClient,
    isSavingPrompt,
    isLoadingClients,
    clientsError,
    selectedTemplate,
    referenceDocument,
    aiPrompts,
    requirementsOption,
    requirementsText,
    selectedKnowledgeDocuments,
    documentRequirements,
    generationStatus,
    generationProgress,
    documentId,
    clients,
    clientSearchOpen,
    selectedClient,
    clientData,
    isNewClient,
    selectedClientId,
    sites,
    offers,
    selectedSiteId,
    setSelectedSiteId,
    promptTemplates,
    promptSearchOpen,
    selectedPrompt,
    promptData,
    isNewPrompt,
    isPromptDialogOpen,
    projectInfo,
    eventSourceRef,
    user, // Current authenticated user
    templates, // Available templates
    documentLogo,
    documentBranding,
    offerDetailsData,
    tenderDetails,

    // Methods
    setCurrentStep,
    setIsGenerating,
    setIsLoading,
    setIsLoadingClients,
    setClientsError,
    setSelectedTemplate,
    setReferenceDocument,
    handlePromptChange,
    setRequirementsOption,
    setRequirementsText,
    setSelectedKnowledgeDocuments,
    setDocumentRequirements,
    setGenerationStatus,
    setGenerationProgress,
    setDocumentId,
    setClients,
    setClientSearchOpen,
    setSelectedClient,
    setClientData,
    setIsNewClient,
    setSelectedClientId,
    setSites: setSitesWithPersistence,
    setOffers: setOffersWithPersistence,
    updateOfferRateData,
    setIsSavingClient,
    tenderName,
    setTenderName,
    tenderId,
    setTenderId,
    tenderSiteIds,
    setTenderSiteIds,
    setTenderDetails,
    setPromptTemplates,
    setPromptSearchOpen,
    setSelectedPrompt,
    setPromptData,
    setIsNewPrompt,
    setIsSavingPrompt,
    setIsPromptDialogOpen,
    setProjectInfo,
    handleClientInputChange,
    handleProjectInfoChange,
    handleBudgetChange,
    handleBudgetRangeChange,
    handleClientSelect,
    handlePromptSelect,
    handlePromptInputChange,
    saveClient,
    savePromptTemplate,
    loadClients,
    handleGenerate,
    handleNext,
    handleBack,
    handleRemoveReference,
    handleTemplateSelect,
    isCurrentStepValid,
    setDocumentLogo,
    setDocumentBranding,
    clearSessionData,
    setOfferDetailsData,
    updateOfferCostData,

    // Workflow management functions
    loadTenderWorkflow,
    enterEditMode,
    saveCurrentStepData,
    createTenderWorkflow,
    updateCurrentStep,
    isLoadingTender,

    // US-033: Navigation validation
    completedSteps,
    canNavigateToStep,
    termSeries,
    setTermSeries,
    proposedTermSeries,
    goToStep,
    registerStepFlush,

    // Salesforce import state
    sfOpportunityId,
    setSfOpportunityId,
    sfTenderId,
    setSfTenderId,
    sfImportExists,
    setSfImportExists,
    sfImportedAt,
    setSfImportedAt,
  } as any; // Type assertion to allow new properties

  return (
    <DocumentFormContext.Provider value={contextValue}>
      {children}
    </DocumentFormContext.Provider>
  );
};
